import { useCallback } from 'react';
import { FilterDocuments } from 'shared/constants';
import {
  DocumentOrganizationKeys,
  FilterDocument,
  FilterDocumentDateFilter,
  FilterDocumentDurationFilter,
  FilterOptionKeys,
} from 'shared/types/filter-document';
import { ID } from 'shared/types/id';
import { noTagId } from 'shared/types/orderable-section';
import { GroupByMode, SortingMode } from 'shared/types/sorting-mode';
import { TaskPriorityOptions, TaskType } from 'shared/types/task-base';

export const useOnFilterOption = <
  GBM extends GroupByMode,
  SM extends SortingMode,
  F extends FilterOptionKeys,
>(
  filterDocument: FilterDocument<GBM, SM, F>,
  defaultFilterDocument: FilterDocument<GBM, SM, F> = {},
  callback: (updatedDocument: FilterDocument<GBM, SM, F>) => void,
) =>
  useCallback(
    (options: {
      id: ID;
      value?: string | string[];
      durationFilter?: FilterDocumentDurationFilter | null;
      dateFilter?: FilterDocumentDateFilter | null;
    }) => {
      const { id, value: val, durationFilter, dateFilter } = options;
      const valArray = !val ? null : Array.isArray(val) ? val : [val];
      const updatedDocument = structuredClone(filterDocument) as FilterDocument<
        GBM,
        SM
      >;
      const defaultDoc = defaultFilterDocument as FilterDocument<GBM, SM>;

      if (id === DocumentOrganizationKeys.SortingMode) {
        updatedDocument[DocumentOrganizationKeys.SortingMode] =
          (valArray?.[0] as SM | undefined) ?? null;
      }

      if (id === DocumentOrganizationKeys.GroupByMode) {
        updatedDocument[DocumentOrganizationKeys.GroupByMode] =
          (valArray?.[0] as GBM | undefined) ?? null;
      }

      if (id === FilterDocuments.ResetFilterDocumentID) {
        updatedDocument[DocumentOrganizationKeys.Filters] =
          defaultFilterDocument[DocumentOrganizationKeys.Filters];
      }

      if (id === FilterOptionKeys.LifeArea) {
        updatedDocument[DocumentOrganizationKeys.Filters] ??= {};
        const defaultVal =
          defaultDoc.filters?.[FilterOptionKeys.LifeArea]?.include;
        const valToUse =
          !valArray?.length && defaultVal?.length ? defaultVal : valArray;

        updatedDocument[DocumentOrganizationKeys.Filters][
          FilterOptionKeys.LifeArea
        ] = valToUse?.length
          ? {
              include: valToUse,
            }
          : null;
      }
      if (id === FilterOptionKeys.Priority) {
        updatedDocument[DocumentOrganizationKeys.Filters] ??= {};
        const defaultVal =
          defaultDoc.filters?.[FilterOptionKeys.Priority]?.include;
        const valToUse =
          !valArray?.length && defaultVal?.length ? defaultVal : valArray;

        updatedDocument[DocumentOrganizationKeys.Filters][
          FilterOptionKeys.Priority
        ] = valToUse?.length
          ? {
              include: valToUse as TaskPriorityOptions[],
            }
          : null;
      }
      if (id === FilterOptionKeys.Type) {
        updatedDocument[DocumentOrganizationKeys.Filters] ??= {};
        const defaultVal = defaultDoc.filters?.[FilterOptionKeys.Type]?.include;
        const valToUse =
          !valArray?.length && defaultVal?.length ? defaultVal : valArray;

        updatedDocument[DocumentOrganizationKeys.Filters][
          FilterOptionKeys.Type
        ] = valToUse?.length
          ? {
              include: valToUse as TaskType[],
            }
          : null;
      }

      if (id === FilterOptionKeys.Tag) {
        updatedDocument[DocumentOrganizationKeys.Filters] ??= {};
        const defaultVal = defaultDoc.filters?.[FilterOptionKeys.Tag];

        const noTag = valArray?.includes(noTagId) ?? false;
        const include = valArray?.filter((val) => val !== noTagId) ?? [];

        updatedDocument[DocumentOrganizationKeys.Filters][
          FilterOptionKeys.Tag
        ] =
          !valArray?.length &&
          !defaultVal &&
          updatedDocument[DocumentOrganizationKeys.Filters]
            ? null
            : !valArray?.length && defaultVal
              ? // If clearing (empty array) and default exists, use default object
                defaultVal
              : {
                  include: include.length ? (include as string[]) : null,
                  noTag: noTag ? true : null,
                };
      }

      if (id === FilterOptionKeys.Goal) {
        updatedDocument[DocumentOrganizationKeys.Filters] ??= {};
        const defaultVal = defaultDoc.filters?.[FilterOptionKeys.Goal]?.include;
        const valToUse =
          !valArray?.length && defaultVal?.length ? defaultVal : valArray;

        updatedDocument[DocumentOrganizationKeys.Filters][
          FilterOptionKeys.Goal
        ] = valToUse?.length
          ? {
              include: valToUse,
            }
          : null;
      }

      if (id === FilterOptionKeys.Duration) {
        updatedDocument[DocumentOrganizationKeys.Filters] ??= {};
        const defaultVal = defaultDoc.filters?.[FilterOptionKeys.Duration];

        const newVal =
          !durationFilter && defaultVal ? defaultVal : durationFilter;
        updatedDocument[DocumentOrganizationKeys.Filters][
          FilterOptionKeys.Duration
        ] = newVal ? newVal : null;
      }

      if (id === FilterOptionKeys.Date) {
        updatedDocument[DocumentOrganizationKeys.Filters] ??= {};
        const defaultVal = defaultDoc.filters?.[FilterOptionKeys.Date];

        const newVal = !dateFilter && defaultVal ? defaultVal : dateFilter;
        updatedDocument[DocumentOrganizationKeys.Filters][
          FilterOptionKeys.Date
        ] = newVal ? newVal : null;
      }

      if (id === FilterOptionKeys.Deadline) {
        updatedDocument[DocumentOrganizationKeys.Filters] ??= {};
        const defaultVal = defaultDoc.filters?.[FilterOptionKeys.Deadline];
        const newVal = !dateFilter && defaultVal ? defaultVal : dateFilter;
        updatedDocument[DocumentOrganizationKeys.Filters][
          FilterOptionKeys.Deadline
        ] = newVal ? newVal : null;
      }

      if (id === FilterOptionKeys.GoalActive) {
        updatedDocument[DocumentOrganizationKeys.Filters] ??= {};
        const defaultVal = defaultDoc.filters?.[FilterOptionKeys.GoalActive];
        const newVal = !dateFilter && defaultVal ? defaultVal : dateFilter;
        updatedDocument[DocumentOrganizationKeys.Filters][
          FilterOptionKeys.GoalActive
        ] = newVal ? newVal : null;
      }

      callback(updatedDocument as FilterDocument<GBM, SM, F>);
    },
    [callback, defaultFilterDocument, filterDocument],
  );
